<script setup lang="ts">
    // @ts-expect-error raison: lib externe mal typée
    import { defineProps, defineEmits, defineExpose, computed } from "vue";
    import { reactive, watch } from 'vue';
    import moment from 'moment';
    import CustomUploadInput from '@/components/CustomUploadInput.vue';
    import { AMOUNT_FORMAT_CONFIG, PERCENTAGE_FORMAT_CONFIG } from '@/utils';

    const props = defineProps<{
        modelValue: {
            id?: number | null;
            nom: string;
            prenom: string;
            telephone: string;
            email: string;
            fonction: string;
            date_naissance: string | null;
            age: number;
            date_engagement: string | null;
            anciennete: number;
            collectivite_id: number | null;
            salaire: string;
            quotite_cessible_pourcent: string;
            quotite_cessible_montant: string;
            photo: any;
            photo_preview: string;
        };
        collectivites?: any[];
        collectiviteId?: number | null;
        showCollectiviteField?: boolean;
        fullWidth?: boolean;
        errors?: {
            nom?: string;
            prenom?: string;
            telephone?: string;
            email?: string;
            date_naissance?: string;
            age?: string;
            date_engagement?: string;
            anciennete?: string;
            collectivite_id?: string;
            salaire?: string;
            quotite_cessible_pourcent?: string;
            quotite_cessible_montant?: string;
        };
    }>();

    const emit = defineEmits<{
        (e: 'update:modelValue', value: typeof props.modelValue): void;
        (e: 'validate'): void;
    }>();

    const localFormData = reactive({ ...props.modelValue });

    // Synchroniser les props avec les données locales
    watch(() => props.modelValue, (newValue) => {
        Object.assign(localFormData, newValue);
    }, { deep: true });

    // Émettre les changements vers le parent
    watch(localFormData, (newValue) => {
        emit('update:modelValue', { ...newValue });
        emit('validate');
    }, { deep: true });

    // Si collectiviteId est fourni en prop, l'utiliser automatiquement
    watch(() => props.collectiviteId, (newValue) => {
        if (newValue && !props.showCollectiviteField) {
            localFormData.collectivite_id = newValue;
        }
    }, { immediate: true });

    // Calculer l'âge automatiquement
    watch(() => localFormData.date_naissance, (newDate) => {
        if (newDate) {
            const birthDate = moment(newDate);
            if (birthDate.isValid()) {
                localFormData.age = moment().diff(birthDate, 'years');
            }
        }
    });

    // Calculer l'ancienneté automatiquement
    watch(() => localFormData.date_engagement, (newDate) => {
        if (newDate) {
            const engagementDate = moment(newDate);
            if (engagementDate.isValid()) {
                localFormData.anciennete = moment().diff(engagementDate, 'years');
            }
        }
    });

    function doCalculateQuotiteCessible() {
        const salaire = parseFloat(localFormData.salaire);
        const quotitePourcent = parseFloat(localFormData.quotite_cessible_pourcent);

        if (!isNaN(salaire) && !isNaN(quotitePourcent) && salaire > 0 && quotitePourcent > 0) {
            localFormData.quotite_cessible_montant = String(salaire * (quotitePourcent / 100));
        } else {
            localFormData.quotite_cessible_montant = '';
        }
    }

    function onFileUploaded(uploadedFile: { file: any, file_name: string, file_preview: string }) {
        localFormData.photo_preview = uploadedFile.file_preview;
        localFormData.photo = { file: uploadedFile.file, name: uploadedFile.file_name };
    }

    // Exposer les données pour validation externe
    defineExpose({
        formData: localFormData
    });
</script>

<template>
    <div class="user-form-component">
        <!-- Informations personnelles -->
        <div :class="fullWidth ? '' : 's10_mobile container-full-2'">
            <h2 class="flex align-items-center gap-4 font-medium text-primary-color my-5">
                <img src="/icons/icon-info-perso.svg" class="size-l" alt="" />
                Informations personnelles
            </h2>

            <div class="form-field-floating reactive active my-5">
                <input type="text" class="form-input" name="nom" id="user_nom" v-model="localFormData.nom" placeholder="" />
                <label for="user_nom">Nom <b class="color-danger">*</b></label>
            </div>
            <span v-if="errors?.nom" class="text-red">{{ errors.nom }}</span>

            <div class="form-field-floating lg reactive active my-5">
                <input type="text" class="form-input" name="prenom" id="user_prenom" v-model="localFormData.prenom" placeholder="" />
                <label for="user_prenom">Prénom <b class="color-danger">*</b></label>
            </div>
            <span v-if="errors?.prenom" class="text-red">{{ errors.prenom }}</span>

            <div class="form-field-split active my-5">
                <custom-phone-input v-model="localFormData.telephone" id="user_telephone" classesNames="half" />
                <hr class="hr" />
                <div class="form-field-floating lg reactive half">
                    <input type="email" class="form-input" name="email" id="user_email" v-model="localFormData.email" placeholder="" />
                    <label for="user_email">Adresse Email <b class="color-danger">*</b></label>
                </div>
            </div>
            <span v-if="errors?.telephone" class="text-red">{{ errors.telephone }}</span>
            <span v-if="errors?.email" class="text-red">{{ errors.email }}</span>

            <div class="form-field-floating lg reactive active my-5">
                <input type="text" class="form-input" name="fonction" id="user_fonction" v-model="localFormData.fonction" placeholder="" />
                <label for="user_fonction">Fonction</label>
            </div>

            <div class="form-field-split active my-5">
                <div class="form-field-floating reactive half">
                    <input type="date" class="form-input" name="date_naissance" id="user_date_naissance" v-model="localFormData.date_naissance" placeholder="" />
                    <label for="user_date_naissance">Date de naissance <b class="color-danger">*</b></label>
                </div>
                <hr class="hr" />
                <div class="form-field-floating reactive half">
                    <input type="text" class="form-input" name="age" id="user_age" v-model="localFormData.age" placeholder="" readonly />
                    <label for="user_age">Age <b class="color-danger">*</b></label>
                </div>
            </div>
            <span v-if="errors?.date_naissance" class="text-red">{{ errors.date_naissance }}</span>
            <span v-if="errors?.age" class="text-red">{{ errors.age }}</span>

            <div class="form-field-split active my-5">
                <div class="form-field-floating reactive half">
                    <input type="date" class="form-input" name="date_engagement" id="user_date_engagement" v-model="localFormData.date_engagement" placeholder="" />
                    <label for="user_date_engagement">Date d'engagement <b class="color-danger">*</b></label>
                </div>
                <hr class="hr" />
                <div class="form-field-floating reactive half">
                    <input type="text" class="form-input" name="anciennete" id="user_anciennete" v-model="localFormData.anciennete" placeholder="" readonly />
                    <label for="user_anciennete">Ancienneté <b class="color-danger">*</b></label>
                </div>
            </div>
            <span v-if="errors?.date_engagement" class="text-red">{{ errors.date_engagement }}</span>
            <span v-if="errors?.anciennete" class="text-red">{{ errors.anciennete }}</span>
        </div>

        <!-- Collectivité (conditionnellement affiché) -->
        <div v-if="showCollectiviteField" :class="fullWidth ? '' : 's10_mobile container-full-2'">
            <h2 class="flex align-items-center gap-4 font-medium text-primary-color my-5">
                <img src="/icons/icon-info-collectivite.svg" class="size-l" alt="" />
                Collectivité
            </h2>

            <div class="form-field-floating active my-4">
                <select class="form-input-select" name="collectivite_id" id="user_collectivite_id" v-model="localFormData.collectivite_id">
                    <option :value="null" selected>Sélectionnez une collectivité</option>
                    <option :value="collectivite.id" v-for="(collectivite, index) in collectivites" :key="'collectivite_' + index">
                        {{ collectivite.nom }}
                    </option>
                </select>
                <label for="user_collectivite_id">Collectivité <b class="color-danger">*</b></label>
            </div>
            <span v-if="errors?.collectivite_id" class="text-red">{{ errors.collectivite_id }}</span>
        </div>

        <!-- Engagement -->
        <div :class="fullWidth ? '' : 's10_mobile container-full-2'">
            <h2 class="flex align-items-center gap-4 font-medium text-primary-color my-5">
                <img src="/icons/icon-engagement.svg" class="size-l" alt="" />
                Engagement
            </h2>

            <div class="form-field-floating reactive active my-5">
                <money3 class="form-input" v-model="localFormData.salaire" name="salaire" id="user_salaire" placeholder="" v-bind="AMOUNT_FORMAT_CONFIG" @blur="doCalculateQuotiteCessible"></money3>
                <label for="user_salaire">Salaire brut <b class="color-danger">*</b></label>
                <span>F&nbsp;CFA</span>
            </div>
            <span v-if="errors?.salaire" class="text-red">{{ errors.salaire }}</span>

            <div class="form-field-split active my-5">
                <div class="form-field-floating reactive half">
                    <money3 class="form-input" v-model="localFormData.quotite_cessible_pourcent" name="quotite_cessible_pourcent" id="user_quotite_cessible_pourcent" placeholder="" v-bind="PERCENTAGE_FORMAT_CONFIG" @blur="doCalculateQuotiteCessible"></money3>
                    <label for="user_quotite_cessible_pourcent">Quotité cessible max <b class="color-danger">*</b></label>
                    <span>%</span>
                </div>
                <hr class="hr" />
                <div class="form-field-floating reactive half">
                    <money3 class="form-input" v-model="localFormData.quotite_cessible_montant" name="quotite_cessible_montant" id="user_quotite_cessible_montant" placeholder="" v-bind="AMOUNT_FORMAT_CONFIG" readonly></money3>
                    <label for="user_quotite_cessible_montant">Valeur <b class="color-danger">*</b></label>
                    <span>F&nbsp;CFA</span>
                </div>
            </div>
            <span v-if="errors?.quotite_cessible_pourcent" class="text-red">{{ errors.quotite_cessible_pourcent }}</span>
            <span v-if="errors?.quotite_cessible_montant" class="text-red">{{ errors.quotite_cessible_montant }}</span>
        </div>

        <!-- Photo -->
        <div :class="fullWidth ? '' : 's10_mobile container-full-2'">
            <h2 class="flex align-items-center gap-4 font-medium text-primary-color my-5">
                <img src="/icons/icon-photo.svg" class="size-l" alt="" />
                Photo
            </h2>

            <div class="custom-upload-size margin-auto flex mb-8">
                <custom-upload-input
                    id="user_photo"
                    label="Photo"
                    type="IMAGE"
                    size="small"
                    :preview="localFormData.photo_preview"
                    @uploaded="onFileUploaded"
                />
            </div>
        </div>
    </div>
</template>
